/**
 * Live TV cart source sync — keeps _itemSource honest for two stale-basket cases:
 *
 * 1. TV → MA: a COA card stamps properties[_itemSource]=TV at add-to-cart
 *    (live-tv-auction-product-form.liquid), a missed-auction card stamps MA (app.js). The stamp is
 *    decided at add time and never revisited, so an item added while its lot was on air keeps
 *    _itemSource=TV even after that lot ends. Place the order in that state and the two consumers
 *    disagree: webhooks.orders skips the live_tv_order-{code} tag because no line matches the COA
 *    snapshot, while the ERP import still reads the line as TV (Shopify_ImportOrder_BP.sql maps
 *    TV → 'TV', MA → 'WEBC').
 * 2. MA → FPC: a missed-auction line can sit in the basket for days. Missed Auctions is a rolling
 *    last-24-hours list, so by checkout the auction may be long gone from it, but the line still
 *    claims _itemSource=MA with the old _auctionCode, and — for a Buy All add — still carries the
 *    bundle properties the Buy All discount function keys off (_buyAllApplicableProductCodes,
 *    _buyAllDiscount). Once the auction has dropped off the list the line should look like a plain
 *    full-price add: _itemSource=FPC, every MA/Buy-All-only property removed.
 *
 * Line item properties are frozen once the order exists — no Admin API mutation rewrites them —
 * so the cart is the last place either source can still be corrected.
 *
 * Runs from the app embed, so it is present on every page: one pass on load, another whenever the tab
 * becomes visible again, and a poll while the shopper sits on the cart page waiting to check out.
 * Once they are inside Shopify checkout nothing here can reach the line any more.
 *
 * Safety rule: only flip when proof is positive.
 * - TV → MA: a COA snapshot naming a different auction is proof the lot is gone. "Nothing on air"
 *   is not — /apps/live-tv/currently-on-air answers isLive:false from its auth-fallback branch when
 *   Shopify auth is momentarily unavailable, indistinguishable from a real end, so that answer is
 *   confirmed by a second read before anything is rewritten (same guard as
 *   NOT_LIVE_POLLS_BEFORE_EMPTY in live-tv-currently-on-air-updater.js).
 * - MA → FPC: a last-24-hours read naming other codes but not this one is proof. A read with zero
 *   codes at all gets the same double-read treatment — a blip there would otherwise read as "every
 *   missed-auction line in every basket is stale" all at once.
 * Any other uncertainty — endpoint unreachable, malformed payload, a line without an _auctionCode —
 * leaves the cart untouched: a missed flip is a reporting mismatch or a stale discount, a wrong flip
 * sells a live or still-valid lot as something it is not.
 *
 * Set localStorage ltv_cart_source_debug=1 for a console trace.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-live-tv-cart-source-sync]';
  var TV_SOURCE = 'TV';
  var MA_SOURCE = 'MA';
  var FPC_SOURCE = 'FPC';
  var ITEM_SOURCE_KEY = '_itemSource';
  var AUCTION_CODE_KEY = '_auctionCode';
  /** Removed outright (not just overwritten) when a line flips MA → FPC: it should look like a
   *  plain full-price add, and stale Buy All properties are what would let the checkout discount
   *  function keep granting a bundle price after the bundle itself is no longer offered. */
  var MA_ONLY_PROPERTY_KEYS = [
    AUCTION_CODE_KEY,
    '_isBuyAll',
    '_discountCode',
    '_buyAllPrice',
    '_buyAllDiscount',
    '_buyAllApplicableProductCodes',
    '_pnp',
  ];
  var BUY_ALL_KEY = '_isBuyAll';
  var BUY_ALL_CODES_KEY = '_buyAllApplicableProductCodes';
  var DEFAULT_API_URL = '/apps/live-tv/currently-on-air';
  var DEFAULT_LAST24_API_URL = '/apps/live-tv/last-24-hours';
  /* Matches the COA updater's cadence — the shopper's cart page should not learn of an auction end
     later than the Live TV page would. */
  var POLL_INTERVAL_MS = 10000;
  /* Gap between the two reads that have to agree before "nothing on air" counts as an auction end.
     The COA updater spends two poll intervals on the same doubt; a cart pass takes the narrower
     window so the rewrite still lands before the shopper clicks checkout, and any pass that guesses
     wrong here costs the shopper the end-of-auction reprice — hence two reads rather than one.
     Reused as-is for the MA → FPC "zero codes at all" recheck, same reasoning. */
  var EMPTY_CONFIRM_DELAY_MS = 8000;
  /* One pass rewrites at most this many lines per source; a cart cannot legitimately hold more
     stale lines than this, so the cap only ever stops a loop that has stopped making progress. */
  var MAX_FLIPS_PER_PASS = 20;
  var MAX_SNAPSHOT_DEPTH = 8;
  var DEBUG_KEY = 'ltv_cart_source_debug';

  var apiUrl = DEFAULT_API_URL;
  var last24ApiUrl = DEFAULT_LAST24_API_URL;
  var passRunning = false;
  var pollTimer = null;
  /* Cart line keys whose rewrite was rejected. A successful change mints a new key, so a key can
     only still be here because it failed — without this the poll would re-attempt the same rejected
     line every tick for as long as the shopper stays on the page. Cleared by the next page load. */
  var failedLineKeys = {};

  function isDebugEnabled() {
    try {
      return typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function debugLog(label, detail) {
    if (!isDebugEnabled()) return;
    if (detail !== undefined) console.log('[LTV][CartSource] ' + label, detail);
    else console.log('[LTV][CartSource] ' + label);
  }

  /** Auction codes arrive from AMS, Kafka and Liquid — compare them case/whitespace-insensitively. */
  function normCode(v) {
    return v == null ? '' : String(v).trim().toLowerCase();
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function isCartPage() {
    var path = String(window.location.pathname || '').replace(/\/+$/, '');
    return /(^|\/)cart$/.test(path);
  }

  // ===========================================================================
  // CURRENTLY ON AIR
  // ===========================================================================

  /**
   * Every auction code anywhere in the COA payload, keyed by name rather than by a fixed path:
   * the main lot carries auctionCode/packageCode, group and MLA rows carry auctionCode plus the
   * mlaAuctionCode / primeAuctionCode aliases, and AMS varies the casing and nests mlauctions rows
   * inside group rows (see the parsing in live-tv-page.liquid). Walking by key name means an
   * unfamiliar shape yields extra codes to match against, never a missing one — the failure
   * direction that leaves a live line alone.
   */
  function collectAuctionCodes(node, codes, depth) {
    if (!node || typeof node !== 'object' || depth > MAX_SNAPSHOT_DEPTH) return codes;
    if (Array.isArray(node)) {
      for (var i = 0; i < node.length; i++) collectAuctionCodes(node[i], codes, depth + 1);
      return codes;
    }
    var keys = Object.keys(node);
    for (var k = 0; k < keys.length; k++) {
      var key = keys[k];
      var value = node[key];
      var lower = key.toLowerCase();
      if (value != null && typeof value === 'object') {
        collectAuctionCodes(value, codes, depth + 1);
      } else if (lower === 'packagecode' || lower.indexOf('auctioncode') !== -1) {
        var code = normCode(value);
        if (code) codes[code] = true;
      }
    }
    return codes;
  }

  /**
   * @returns {Promise<{known: boolean, live: boolean, codes: Object}>} `known:false` means the
   * endpoint could not be read — the caller must treat that as "no information", not as an end.
   */
  function readCurrentlyOnAir() {
    return fetch(apiUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('currently-on-air responded ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json || json.success !== true) return { known: false, live: false, codes: {} };
        var live = json.isLive === true && !!json.data;
        return {
          known: true,
          live: live,
          codes: live ? collectAuctionCodes(json.data, {}, 0) : {},
        };
      })
      .catch(function (error) {
        debugLog('currently-on-air read failed', error && error.message);
        return { known: false, live: false, codes: {} };
      });
  }

  // ===========================================================================
  // LAST 24 HOURS / MISSED AUCTIONS (feeds the MA → FPC flip)
  // ===========================================================================

  /**
   * Every auction code still offered on the Missed Auctions page right now: the top-level auction
   * plus its nested groupauctions/mlauctions codes. TJC's last-24-hours route already drops
   * out-of-stock/unmatched auctions from the response entirely (auction-oos-filter.ts) rather than
   * annotating them, so presence in the payload is enough — no separate visibility flag to check.
   * An auction whose hour has simply aged out of the last-24-hours window will not appear in the
   * payload at all, which this "not found" check already covers without needing a per-item
   * timestamp.
   *
   * @returns {Promise<{known: boolean, codes: Object}>} `known:false` means the endpoint could not
   * be read (not configured, request failed, malformed payload) — treat as "no information".
   */
  function readCurrentMissedAuctionCodes() {
    return fetch(last24ApiUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('last-24-hours responded ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json || json.success !== true) return { known: false, codes: {} };
        var hours = (json.data && json.data.hours) || [];
        var codes = {};
        for (var h = 0; h < hours.length; h++) {
          var auctions = hours[h].auctions || [];
          for (var a = 0; a < auctions.length; a++) {
            addAuctionCodeAndChildren(auctions[a], codes);
          }
        }
        return { known: true, codes: codes };
      })
      .catch(function (error) {
        debugLog('last-24-hours read failed', error && error.message);
        return { known: false, codes: {} };
      });
  }

  /** One auction's own code plus every nested group/MLA code beneath it. */
  function addAuctionCodeAndChildren(auction, codes) {
    if (!auction) return;
    var code = normCode(auction.auctionCode);
    if (code) codes[code] = true;
    var groups = auction.groupauctions || auction.groupAuctions || [];
    for (var g = 0; g < groups.length; g++) {
      var ga = groups[g];
      var gaCode = normCode(ga.auctionCode);
      if (gaCode) codes[gaCode] = true;
      var mlas = ga.mlauctions || ga.mlaAuctions || [];
      for (var m = 0; m < mlas.length; m++) {
        var mlaCode = normCode(mlas[m].auctionCode);
        if (mlaCode) codes[mlaCode] = true;
      }
    }
  }

  // ===========================================================================
  // CART
  // ===========================================================================

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js responded ' + res.status);
      return res.json();
    });
  }

  /** Live TV lines that can be verified: source TV and an auction code to check it against. */
  function tvLines(cart) {
    var items = (cart && cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i].properties || {};
      if (String(props[ITEM_SOURCE_KEY] || '').trim().toUpperCase() !== TV_SOURCE) continue;
      if (!normCode(props[AUCTION_CODE_KEY])) continue;
      out.push({ line: i + 1, item: items[i] });
    }
    return out;
  }

  function firstEndedLine(cart, coa) {
    var entries = tvLines(cart);
    for (var i = 0; i < entries.length; i++) {
      if (failedLineKeys[entries[i].item.key]) continue;
      var code = normCode(entries[i].item.properties[AUCTION_CODE_KEY]);
      if (!coa.codes[code]) return entries[i];
    }
    return null;
  }

  /** Missed-auction lines that can be verified: source MA and an auction code to check it against. */
  function maLines(cart) {
    var items = (cart && cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i].properties || {};
      if (String(props[ITEM_SOURCE_KEY] || '').trim().toUpperCase() !== MA_SOURCE) continue;
      if (!normCode(props[AUCTION_CODE_KEY])) continue;
      out.push({ line: i + 1, item: items[i] });
    }
    return out;
  }

  function firstStaleMaLine(cart, currentMa) {
    var entries = maLines(cart);
    for (var i = 0; i < entries.length; i++) {
      if (failedLineKeys[entries[i].item.key]) continue;
      var code = normCode(entries[i].item.properties[AUCTION_CODE_KEY]);
      if (!currentMa.codes[code]) return entries[i];
    }
    return null;
  }

  /** Cart Ajax wants the numeric id; /cart.js hands back a number, the theme may hand back a gid. */
  function normalizeSellingPlan(planValue) {
    if (planValue == null) return planValue;
    var s = String(planValue);
    var m = s.match(/SellingPlan\/(\d+)/i);
    if (m) return Number(m[1]);
    var n = parseInt(s, 10);
    return isNaN(n) ? s : n;
  }

  /**
   * /cart/change.js replaces the line's whole property set, so every existing property is resent
   * with only _itemSource rewritten. selling_plan is resent for the same reason: Budget Pay lines
   * must keep their plan through the change. Rewriting properties changes the line key, and Shopify
   * merges the result into an existing identical MA line when there is one.
   */
  function flipLineToMissedAuction(target) {
    var item = target.item;
    var source = item.properties || {};
    var properties = {};
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) properties[key] = source[key];
    }
    properties[ITEM_SOURCE_KEY] = MA_SOURCE;

    var body = {
      line: target.line,
      quantity: item.quantity,
      properties: properties,
    };
    var plan =
      item.selling_plan_allocation &&
      item.selling_plan_allocation.selling_plan &&
      item.selling_plan_allocation.selling_plan.id;
    if (plan != null && plan !== '') body.selling_plan = normalizeSellingPlan(plan);

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!res.ok) {
            throw new Error(data.description || data.message || 'cart/change.js responded ' + res.status);
          }
          return data;
        });
    });
  }

  /**
   * MA → FPC: the line becomes a plain full-price add, so every MA/Buy-All-only property is
   * dropped outright rather than just having _itemSource overwritten. Same /cart/change.js
   * mechanics as flipLineToMissedAuction — full property set resent, selling_plan preserved.
   */
  function flipLineToFullPriceCart(target) {
    var item = target.item;
    var source = item.properties || {};
    var properties = {};
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) properties[key] = source[key];
    }
    properties[ITEM_SOURCE_KEY] = FPC_SOURCE;
    for (var i = 0; i < MA_ONLY_PROPERTY_KEYS.length; i++) {
      delete properties[MA_ONLY_PROPERTY_KEYS[i]];
    }

    var body = {
      line: target.line,
      quantity: item.quantity,
      properties: properties,
    };
    var plan =
      item.selling_plan_allocation &&
      item.selling_plan_allocation.selling_plan &&
      item.selling_plan_allocation.selling_plan.id;
    if (plan != null && plan !== '') body.selling_plan = normalizeSellingPlan(plan);

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!res.ok) {
            throw new Error(data.description || data.message || 'cart/change.js responded ' + res.status);
          }
          return data;
        });
    });
  }

  /**
   * Flips one line per iteration, re-reading the cart in between: a successful change renumbers the
   * lines (and may merge two of them), so indexes from an earlier read cannot be reused. A rejected
   * line is set aside and the pass moves on to the next one, which is also what makes the recursion
   * terminate — every iteration either rewrites a line or removes it from the candidates.
   */
  function flipEndedLines(coa, progress) {
    if (progress.attempts >= MAX_FLIPS_PER_PASS) {
      debugLog('attempt cap reached, stopping pass', progress);
      return Promise.resolve(progress.flipped);
    }
    return fetchCart().then(function (cart) {
      var target = firstEndedLine(cart, coa);
      if (!target) return progress.flipped;
      var code = target.item.properties[AUCTION_CODE_KEY];
      progress.attempts++;
      return flipLineToMissedAuction(target).then(
        function () {
          progress.flipped++;
          debugLog('flipped line to MA', { line: target.line, sku: target.item.sku, auctionCode: code });
          return flipEndedLines(coa, progress);
        },
        function (error) {
          failedLineKeys[target.item.key] = true;
          console.error('[LTV][CartSource] Could not rewrite _itemSource to MA', {
            line: target.line,
            sku: target.item.sku,
            auctionCode: code,
            error: error && error.message,
          });
          return flipEndedLines(coa, progress);
        },
      );
    });
  }

  /** Same one-at-a-time / re-read-in-between shape as flipEndedLines, targeting stale MA lines. */
  function flipStaleMaLines(currentMa, progress) {
    if (progress.attempts >= MAX_FLIPS_PER_PASS) {
      debugLog('attempt cap reached, stopping pass', progress);
      return Promise.resolve(progress.flipped);
    }
    return fetchCart().then(function (cart) {
      var target = firstStaleMaLine(cart, currentMa);
      if (!target) return progress.flipped;
      var code = target.item.properties[AUCTION_CODE_KEY];
      progress.attempts++;
      return flipLineToFullPriceCart(target).then(
        function () {
          progress.flipped++;
          debugLog('flipped line MA -> FPC', { line: target.line, sku: target.item.sku, auctionCode: code });
          return flipStaleMaLines(currentMa, progress);
        },
        function (error) {
          failedLineKeys[target.item.key] = true;
          console.error('[LTV][CartSource] Could not rewrite _itemSource to FPC', {
            line: target.line,
            sku: target.item.sku,
            auctionCode: code,
            error: error && error.message,
          });
          return flipStaleMaLines(currentMa, progress);
        },
      );
    });
  }

  /** Lines stamped as part of a Buy All bundle: _isBuyAll=true plus the bundle's SKU list. */
  function buyAllLines(cart) {
    var items = (cart && cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i].properties || {};
      if (String(props[BUY_ALL_KEY] || '').trim().toLowerCase() !== 'true') continue;
      if (!String(props[BUY_ALL_CODES_KEY] || '').trim()) continue;
      out.push({ line: i + 1, item: items[i] });
    }
    return out;
  }

  /**
   * The stamp is written when the bundle is added and never revisited, so a shopper who removes
   * the other bundle items keeps a lone line still claiming _isBuyAll=true. The checkout discount
   * function already refuses an incomplete bundle; this keeps the order line's flag honest too.
   * Proof is positive only: a required SKU is absent from a cart we read successfully.
   */
  function firstIncompleteBuyAllLine(cart) {
    var skusInCart = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var sku = normCode(items[i].sku);
      if (sku) skusInCart[sku] = true;
    }
    var entries = buyAllLines(cart);
    for (var e = 0; e < entries.length; e++) {
      if (failedLineKeys[entries[e].item.key]) continue;
      var required = String(entries[e].item.properties[BUY_ALL_CODES_KEY]).split(',');
      for (var r = 0; r < required.length; r++) {
        var code = normCode(required[r]);
        if (code && !skusInCart[code]) return entries[e];
      }
    }
    return null;
  }

  /**
   * Bundle no longer complete: same shape as an incomplete Buy All add (see postBuyAllItemsToCart in
   * live-tv-card-forms.js) — _isBuyAll=false and the full-bundle code list dropped. Same
   * /cart/change.js mechanics as the other flips — full property set resent, selling_plan kept.
   */
  function flipLineToSingleItem(target) {
    var item = target.item;
    var source = item.properties || {};
    var properties = {};
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) properties[key] = source[key];
    }
    properties[BUY_ALL_KEY] = 'false';
    delete properties[BUY_ALL_CODES_KEY];

    var body = {
      line: target.line,
      quantity: item.quantity,
      properties: properties,
    };
    var plan =
      item.selling_plan_allocation &&
      item.selling_plan_allocation.selling_plan &&
      item.selling_plan_allocation.selling_plan.id;
    if (plan != null && plan !== '') body.selling_plan = normalizeSellingPlan(plan);

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!res.ok) {
            throw new Error(data.description || data.message || 'cart/change.js responded ' + res.status);
          }
          return data;
        });
    });
  }

  /** Same one-at-a-time / re-read-in-between shape as flipStaleMaLines, targeting broken bundles. */
  function flipIncompleteBuyAllLines(progress) {
    if (progress.attempts >= MAX_FLIPS_PER_PASS) {
      debugLog('attempt cap reached, stopping pass', progress);
      return Promise.resolve(progress.flipped);
    }
    return fetchCart().then(function (cart) {
      var target = firstIncompleteBuyAllLine(cart);
      if (!target) return progress.flipped;
      progress.attempts++;
      return flipLineToSingleItem(target).then(
        function () {
          progress.flipped++;
          debugLog('flipped line Buy All -> single', { line: target.line, sku: target.item.sku });
          return flipIncompleteBuyAllLines(progress);
        },
        function (error) {
          failedLineKeys[target.item.key] = true;
          console.error('[LTV][CartSource] Could not clear _isBuyAll', {
            line: target.line,
            sku: target.item.sku,
            error: error && error.message,
          });
          return flipIncompleteBuyAllLines(progress);
        },
      );
    });
  }

  /**
   * Nothing visible changes — same variant, price and quantity, and _-prefixed properties are never
   * rendered — but the line keys did change, so themes that key their cart UI off them are told to
   * re-render. cart:refresh carries no payload by contract, so a theme that ignores it is unaffected.
   */
  function notifyThemeOfCartChange() {
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {
      debugLog('cart:refresh dispatch failed', e && e.message);
    }
  }

  // ===========================================================================
  // PASS
  // ===========================================================================

  /**
   * Resolves the TV → MA half: no TV lines, no work. Otherwise reads COA; "live" flips ended lines
   * straight away, "nothing on air" is re-confirmed once before anything is rewritten, because the
   * endpoint's auth-fallback branch answers exactly the same way when Shopify auth is momentarily
   * unavailable.
   */
  function runTvPass(tvCount) {
    if (tvCount === 0) return Promise.resolve(0);
    return readCurrentlyOnAir().then(function (coa) {
      if (!coa.known) return 0;
      if (coa.live) return flipEndedLines(coa, { flipped: 0, attempts: 0 });
      return delay(EMPTY_CONFIRM_DELAY_MS)
        .then(readCurrentlyOnAir)
        .then(function (confirmation) {
          if (!confirmation.known) return 0;
          return flipEndedLines(confirmation, { flipped: 0, attempts: 0 });
        });
    });
  }

  /**
   * Resolves the MA → FPC half: no MA lines, no work. A last-24-hours read with at least one code
   * is trusted immediately. A read with zero codes at all is unusual enough — it would otherwise
   * mean "every missed-auction line in every basket is stale at once" — that it gets the same
   * re-confirm treatment as COA's "nothing on air", so one blip cannot mass-flip baskets.
   */
  function runMaPass(maCount) {
    if (maCount === 0) return Promise.resolve(0);
    return readCurrentMissedAuctionCodes().then(function (currentMa) {
      if (!currentMa.known) return 0;
      if (Object.keys(currentMa.codes).length > 0) return flipStaleMaLines(currentMa, { flipped: 0, attempts: 0 });
      return delay(EMPTY_CONFIRM_DELAY_MS)
        .then(readCurrentMissedAuctionCodes)
        .then(function (confirmation) {
          if (!confirmation.known) return 0;
          return flipStaleMaLines(confirmation, { flipped: 0, attempts: 0 });
        });
    });
  }

  /** Buy All → single: no flagged lines, no work. Needs only the cart itself, no endpoint read. */
  function runBuyAllPass(buyAllCount) {
    if (buyAllCount === 0) return Promise.resolve(0);
    return flipIncompleteBuyAllLines({ flipped: 0, attempts: 0 });
  }

  function runPass(trigger) {
    if (passRunning) {
      debugLog('pass already running, skipping', trigger);
      return Promise.resolve(0);
    }
    passRunning = true;
    debugLog('pass start', trigger);
    /* -1 until a cart is actually read: a failed read must not be mistaken for an empty cart and
       switch polling off for the rest of the visit. */
    var tvCount = -1;
    var maCount = -1;
    var buyAllCount = -1;

    return fetchCart()
      .then(function (cart) {
        tvCount = tvLines(cart).length;
        maCount = maLines(cart).length;
        buyAllCount = buyAllLines(cart).length;
        // Buy All pass first: it needs only the cart, whereas the TV/MA passes can sit through an
        // 8s "nothing on air" re-read — a shopper who removes items and checks out quickly would
        // otherwise beat it. Each pass re-reads the cart fresh, since a flip can renumber/merge
        // lines and indexes from this snapshot cannot be reused.
        return runBuyAllPass(buyAllCount).then(function (buyAllFlipped) {
          return runTvPass(tvCount).then(function (tvFlipped) {
            return runMaPass(maCount).then(function (maFlipped) {
              return buyAllFlipped + tvFlipped + maFlipped;
            });
          });
        });
      })
      .then(function (flipped) {
        if (flipped > 0) notifyThemeOfCartChange();
        return flipped;
      })
      .catch(function (error) {
        console.error('[LTV][CartSource] Cart source sync failed', error && error.message);
        return 0;
      })
      .then(function (flipped) {
        passRunning = false;
        debugLog('pass end', {
          trigger: trigger,
          tvLines: tvCount,
          maLines: maCount,
          buyAllLines: buyAllCount,
          flipped: flipped,
        });
        syncPollingToCart(tvCount, maCount, buyAllCount);
        return flipped;
      });
  }

  // ===========================================================================
  // INIT
  // ===========================================================================

  function startPolling() {
    if (pollTimer !== null) return;
    debugLog('polling started');
    pollTimer = setInterval(function () {
      if (document.hidden) return;
      runPass('poll');
    }, POLL_INTERVAL_MS);
  }

  function stopPolling() {
    if (pollTimer === null) return;
    clearInterval(pollTimer);
    pollTimer = null;
    debugLog('polling stopped');
  }

  /**
   * Polling is confined to the cart page holding a Live TV or missed-auction line: that is the
   * shopper waiting with a stamped line in front of them. Every other page, and every cart without
   * one, is covered by the single pass on load — which is what keeps a storewide app embed off a
   * 10s store-wide poll.
   */
  function syncPollingToCart(tvCount, maCount, buyAllCount) {
    if (tvCount < 0 || maCount < 0 || buyAllCount < 0) return;
    if (isCartPage() && (tvCount > 0 || maCount > 0 || buyAllCount > 0)) startPolling();
    else stopPolling();
  }

  function init() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;
    apiUrl = root.getAttribute('data-currently-on-air-api-url') || DEFAULT_API_URL;
    last24ApiUrl = root.getAttribute('data-last-24-hours-api-url') || DEFAULT_LAST24_API_URL;

    runPass('load');

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) runPass('visible');
    });

    /* The theme announces every cart change with cart:update (budget-pay-livetv.js listens too).
       A remove should clear a broken Buy All flag now, not on the next poll tick. Safe to re-run:
       a pass only rewrites on positive proof, and its own cart:refresh dispatch is a different
       event, so it cannot loop. */
    document.addEventListener('cart:update', function () {
      runPass('cart:update');
    });
  }

  if (window.Shopify && window.Shopify.designMode) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
